import type { InitOptions, Resource } from 'i18next';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '../locales';
import type { LocaleCode, NamespaceName } from '../types';

export interface SharedInitOptions {
  /** Per-app namespace name (e.g. 'admin', 'portal', 'marketing', 'mobile'). */
  readonly appNamespace: NamespaceName;
  /** App-side translations: { [lng]: appNamespace -> object } or { appNamespace -> object } */
  readonly appResources: Record<string, unknown>;
  /** Override the initial locale; otherwise auto-detected. */
  readonly initialLocale?: LocaleCode;
}

export function buildResourceConfig(
  opts: SharedInitOptions,
  namespaces: readonly NamespaceName[],
  resources: Resource,
): InitOptions {
  return {
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    ns: [...namespaces, opts.appNamespace],
    defaultNS: opts.appNamespace,
    fallbackNS: namespaces as unknown as string[],
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    resources: mergeResources(resources, normalizeAppResources(opts)),
    compatibilityJSON: 'v4',
  };
}

function normalizeAppResources(opts: SharedInitOptions): Resource {
  // Accept either { en: { admin: {...} } } or { admin: {...} } and normalise to the former.
  const sample = Object.keys(opts.appResources)[0];
  if (sample !== undefined && SUPPORTED_LOCALES.includes(sample as LocaleCode)) {
    return opts.appResources as Resource;
  }
  return { en: opts.appResources } as Resource;
}

/**
 * Plain-object check used by `deepMergeObjects`. Arrays, dates, regexes, etc.
 * are NOT plain objects — those replace wholesale instead of merging.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Recursive deep merge for nested translation objects. App keys override
 * shared keys at the LEAF level, not at the parent-object level.
 *
 * Example — shared = `{ basics: { title: 'A', save: 'Save' } }`,
 *           app    = `{ basics: { title: 'B' } }`,
 *           result = `{ basics: { title: 'B', save: 'Save' } }`
 *
 * Without this, app's `basics` would REPLACE shared's `basics` entirely,
 * wiping out every key the app didn't redeclare (which was the F12
 * venue-editor.json bug — apps had to mirror ~200 shared strings just to
 * override 5 keys).
 *
 * Arrays / Dates / RegExps replace wholesale because deep-merging them
 * yields unsafe semantics for i18n payloads.
 */
function deepMergeObjects(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const overrideVal = override[key];
    const baseVal = out[key];
    if (isPlainObject(overrideVal) && isPlainObject(baseVal)) {
      out[key] = deepMergeObjects(baseVal, overrideVal);
    } else {
      out[key] = overrideVal;
    }
  }
  return out;
}

/**
 * Merges shared + app resources at language → namespace → key depth, with
 * deep-merging WITHIN each namespace so an app override only replaces the
 * keys it declares (not the entire namespace object).
 *
 * Resource shape: `{ [lng]: { [namespace]: { ...flat-or-nested keys } } }`.
 */
function mergeResources(a: Resource, b: Resource): Resource {
  const out: Resource = {};
  const langs = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const lng of langs) {
    const sharedLng = (a[lng] ?? {}) as Record<string, unknown>;
    const appLng = (b[lng] ?? {}) as Record<string, unknown>;
    const namespaces = new Set([...Object.keys(sharedLng), ...Object.keys(appLng)]);
    const merged: Record<string, unknown> = {};
    for (const ns of namespaces) {
      const sharedNs = sharedLng[ns];
      const appNs = appLng[ns];
      if (isPlainObject(sharedNs) && isPlainObject(appNs)) {
        merged[ns] = deepMergeObjects(sharedNs, appNs);
      } else if (appNs !== undefined) {
        // App declared the namespace but shared didn't (or shared has a non-object) — use app's.
        merged[ns] = appNs;
      } else {
        merged[ns] = sharedNs;
      }
    }
    out[lng] = merged as unknown as Resource[string];
  }
  return out;
}
